import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, model, OnDestroy, OnInit, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { CalculateDocumentLineItemPrices, resolveContractFactorTermViolation } from '@phoenix/common';
import { simpleDeepClone } from '@phx-erp/shared';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { findAllDisabledFields, findFieldByKey } from '../../../../common/utilities';
import { Document, DocumentDirection, DocumentLineItemType, IntrastatBusinessType, SalesTaxCode, StockAction, StoragePlace, TaxLine, Unit } from '../../../../generated/generated-types';
import { SalesTaxCodeDetailService } from '../../../masterdata/tax/services/sales-tax-code-detail.service';
import { ProductPriceInfoExt } from '../../../products/modules/product-price-info/product-price-info-widget/product-price-info.component';
import { ConversionResult } from '../../modules/dli-unit-conversion-drop-down/dli-unit-conversion-drop-down.component';
import { DocumentLineItemService } from '../../services/document-line-item.service';
import { LineItemDataService } from '../../services/line-item-data.service';
import { applyContractFactorToDli, shouldShowContractLineItemFields } from '../../utils/contract-line-item-ui.helper';
import { addEditLineItemFallbackFields } from '../widget-add-edit-document-line-item/fallback-fields';
import { addEditLineItemTextFallbackFields } from '../widget-add-edit-document-line-item/text-fallback-fields';
import type { DocumentLineItemDisplayClass } from '../widget-add-edit-document-line-item/types-interfaces';

/** Line-item field keys that never lock when the line is saved (Intrastat Art des Geschäfts, editable for a Berichtigung). */
const ALWAYS_EDITABLE_LINE_ITEM_FIELDS: string[] = ['intrastatBusinessType'];

@Component({
  selector: 'app-widget-add-edit-line-item-list-detail',
  templateUrl: './widget-add-edit-line-item-list-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
  ]
})
export class WidgetAddEditLineItemListDetailComponent implements OnInit, AfterViewInit, OnDestroy {

  //#region NGX Formly
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fallbackFields: FormlyFieldConfig[];
  public fields = signal<FormlyFieldConfig[]>(null);
  options: FormlyFormOptions = {};

  private readonly destroyRef = inject(DestroyRef);
  decimalPlaces = model<number>();

  //#endregion

  dli = model<DocumentLineItemDisplayClass>(null);
  document = model<Document>(null);
  direction = model<DocumentDirection>(null);
  stockAction = model<StockAction>(null);

  defaultDisabledFields = model<FormlyFieldConfig[]>([]);

  allDisabled = model<boolean>(false);
  ongoingRefreshDependingFields = model<boolean>(false);

  activeProductPriceInfos = model<ProductPriceInfoExt[]>([]);

  activeUnitConversionValue = model<ConversionResult>();
  activeUnitConversions = model<ConversionResult[]>([]);

  cachedSalesTaxCodeId: string;
  cachedStoragePlaceId: string;
  cachedQuantity: number;
  cachedNonDiscountable: boolean;
  cachedUnitId: string | null;
  private contractFormSnapshot: string | undefined;
  cachedBusinessType: IntrastatBusinessType;

  constructor(
    private i18n: I18nService,
    private lineItemDataService: LineItemDataService,
    private salesTaxCodeDetailService: SalesTaxCodeDetailService,
    private dliService: DocumentLineItemService,
    private cdRef: ChangeDetectorRef
  ) {

    effect(() => {
      if (this.dli()?.currentlyInEditMode()) {
        untracked(() => this.disableEnableFormControlsAndRefreshState(this.form, false));
      }
      else {
        untracked(() => this.disableEnableFormControlsAndRefreshState(this.form, true));
      }
    });

    effect(() => {
      this.dli();
      untracked(() => this.publishContractTermViolation());
    });

    effect(() => {
      let doc = this.document();
      if (doc && !this.options.formState?.document)
        this.options.formState.document = doc;
    });

    effect(() => {
      this.document();
      this.dli();
      if (!this.fields()) {
        return;
      }
      untracked(() => this.updateContractTabVisibility());
    });
  }

  ngOnInit(): void {

    if (this.dli()?.type == DocumentLineItemType.TEXT) {
      this.fields.set(addEditLineItemTextFallbackFields(this.i18n));
    } else {
      this.fields.set(addEditLineItemFallbackFields(this.i18n));
    }

    this.setFieldLabelIfNeeded();
    this.defaultDisabledFields.set(findAllDisabledFields(this.fields(), this.dli()));

    this.options.formState = {
      detailComponent: this,
      document: this.document(),
      showContractLineItemFields: shouldShowContractLineItemFields(this.document(), this.dli()),
    };

    this.dli()?.dataChangedTrigger?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleDliDataChanged();
    });
  }

  private handleDliDataChanged(): void {
    this.updateContractTabVisibility();
    this.resetModel();
  }

  /** Called from Formly contract-field hooks when nested contract form values change. */
  onContractFormChanged(): void {
    if (!shouldShowContractLineItemFields(this.document(), this.dli())) {
      return;
    }

    const snapshot = this.readContractFormSnapshot();
    if (snapshot === this.contractFormSnapshot) {
      return;
    }
    this.contractFormSnapshot = snapshot;

    const dli = this.dli();
    const rawFactor = this.form.get('contractDocumentLineItem.contractFactor')?.value;
    applyContractFactorToDli(dli, rawFactor);
    CalculateDocumentLineItemPrices(dli, this.document());
    this.dli()?.detailToListChangeTrigger.next();
    this.lineItemDataService.setDocumentLineItem(dli);
    this.publishContractTermViolation();
    this.cdRef.markForCheck();
  }

  private publishContractTermViolation(): void {
    const dli = this.dli();
    const code = resolveContractFactorTermViolation(dli?.contractDocumentLineItem);
    this.lineItemDataService.setContractTermViolation(
      code ? { position: dli?.positionNumber ?? null, code } : null,
    );
  }

  private readContractFormSnapshot(): string | undefined {
    const group = this.form.get('contractDocumentLineItem');
    if (!group) {
      return undefined;
    }
    return JSON.stringify(group.getRawValue());
  }

  private updateContractTabVisibility(): void {
    const visible = shouldShowContractLineItemFields(this.document(), this.dli());
    if (this.options.formState?.showContractLineItemFields === visible) {
      return;
    }
    this.options.formState.showContractLineItemFields = visible;
    const contractTab = this.fields()?.[0]?.fieldGroup?.find(
      (tab) => tab.props?.attributes?.['data-testid'] === 'formly-dli-contract',
    );
    if (contractTab) {
      this.options.detectChanges?.(contractTab);
    }
    this.cdRef.markForCheck();
  }

  async ngAfterViewInit() {
    this.form.controls['salesTaxCode']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: SalesTaxCode) => {
      let changed: boolean = false;
      if (this.cachedSalesTaxCodeId === x?.id || !this.dli())
        return;

      this.cachedSalesTaxCodeId = x?.id;

      if (!this.allDisabled()) {
        changed = true;
      }
      if (!this.allDisabled() && x?.id) {
        // this is needed when salesTaxCode field is not visible in the list widget
        await this.salesTaxCodeDetailService.getItemDataBySalesTaxCodeId(x?.id);
        let dli = simpleDeepClone(this.dli());
        dli.salesTaxCode = x;
        dli.salesTaxCodeId = x?.id;
        let taxLine: TaxLine = await this.dliService.getTaxLineData(this.document(), dli);

        // if you update dli signal it is not synchronized anymore with list 
        // this.dli.update(dli => ({ ...dli, taxPercentage: detail.value, account: x?.salesTaxCode?.fallbackAccount }));
        this.form.patchValue({
          taxPercentage: taxLine.taxRate,
          account: taxLine.account,
        });
        // this.dli().dataChangedTrigger.next(); //with this line its not working anymore
        changed = true;
      }
      if (changed) {
        this.dli()?.detailToListChangeTrigger.next();
        //without this update of lineItemStatusBar & quantityStoragePlaceInput is not working properly
        this.lineItemDataService.setDocumentLineItem(this.dli());
        this.cdRef.markForCheck();
      }
      this.refreshDependingFields();
    });

    this.form.controls['storagePlace']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: StoragePlace) => {
      if (this.cachedStoragePlaceId === x?.id || !this.dli())
        return;

      this.cachedStoragePlaceId = x?.id;

      if (!x)
        this.dli().storagePlaceId = null;

      this.dli().detailToListChangeTrigger.next();
      // this refreshes the text for statusbar
      this.lineItemDataService.setDocumentLineItem(this.dli());
      // this recalculates the open stock for statusbar
      this.lineItemDataService.refreshOpenStock();
      this.cdRef.markForCheck();
    });

    this.form.controls['quantity']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: number) => {

      if (x === this.cachedQuantity || !this.dli())
        return;
      this.cachedQuantity = x;
      this.dli().quantity = x;

      this.form.patchValue({
        quantity: x,
      });
      this.dli().detailToListChangeTrigger.next();
      this.lineItemDataService.setDocumentLineItem(this.dli());
      this.cdRef.markForCheck();
    });

    this.form.controls['nonDiscountable']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: boolean) => {
      if (this.cachedNonDiscountable === x || !this.dli())
        return;
      this.cachedNonDiscountable = x;
      this.dli.update(dli => {
        dli.nonDiscountable = x;
        dli.discountPercentage = x ? null : dli.discountPercentage;
        dli.discountPercentage2 = x ? null : dli.discountPercentage2;
        return dli;
      });
      this.dli().detailToListChangeTrigger.next();
      this.lineItemDataService.setDocumentLineItem(this.dli());
      this.cdRef.markForCheck();
      this.refreshDependingFields();
    });

    // otherwise products with no unit (various) dont work from detail to list
    this.form.controls['unit']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: Unit) => {
      const nextUnitId = x?.id ?? null;
      if ((this.cachedUnitId ?? null) === nextUnitId || !this.dli())
        return;

      this.cachedUnitId = nextUnitId;
      this.dli.update(dli => {
        dli.unit = x;
        dli.unitId = x?.id;
        dli.unitIdentifier = x?.identifier;
        return dli;
      });

      this.dli().detailToListChangeTrigger.next();
      this.lineItemDataService.setDocumentLineItem(this.dli());
      this.cdRef.markForCheck();
    });

    const quantityControl = this.form.controls['quantity'];
    const quantityLocked = !!this.dli()?.quantityLocked;
    if (quantityControl) {
      if (quantityLocked)
        quantityControl.disable();
      else
        quantityControl.enable();
    }

    // Art des Geschäfts stays editable after save, so a change here has no row-save to ride on — persist it directly.
    this.cachedBusinessType = this.dli()?.intrastatBusinessType;
    this.form.controls['intrastatBusinessType']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: IntrastatBusinessType) => {
      this.autoSaveBusinessType(value);
    });

    // this is needed for open and close of this component (as dropdown)
    this.refreshDependingFields();
    this.disableFormControlsIfNeeded();
    this.contractFormSnapshot = this.readContractFormSnapshot();
  }


  ngOnDestroy(): void {
    this.lineItemDataService.setContractTermViolation(null);
    this.dli.set(null);
  }

  private async autoSaveBusinessType(value: IntrastatBusinessType): Promise<void> {
    const dli = this.dli();
    // Compare to the cache, not dli (Formly mutates dli before the handler fires); in edit mode the row save persists the change.
    if (!dli?.id || dli.currentlyInEditMode?.() || value === this.cachedBusinessType)
      return;
    try {
      await this.dliService.saveItem({ id: dli.id, documentId: dli.documentId ?? this.document()?.id, intrastatBusinessType: value });
    } catch (error: any) {
      console.error('Intrastat AdG auto-save failed', error);
    }
    this.cachedBusinessType = value;
    dli.intrastatBusinessType = value;
    dli.detailToListChangeTrigger?.next();
  }

  resetModel() {
    // Set before resetModel emits, so the load-time valueChanges doesn't read a stale cache and trigger a save.
    this.cachedBusinessType = this.dli()?.intrastatBusinessType;
    this.options.resetModel(this.dli());
    const dli = this.dli();
    this.cachedSalesTaxCodeId = dli?.salesTaxCodeId;
    this.cachedStoragePlaceId = dli?.storagePlaceId ?? null;
    this.cachedQuantity = dli?.quantity;
    this.cachedNonDiscountable = dli?.nonDiscountable;
    this.cachedUnitId = dli?.unitId ?? null;
    this.contractFormSnapshot = this.readContractFormSnapshot();
  }

  disableFormControlsIfNeeded() {
    if (this.dli()?.currentlyInEditMode()) {
      this.disableEnableFormControlsAndRefreshState(this.form, false);
    }
    else {
      this.disableEnableFormControlsAndRefreshState(this.form, true);
    }
  }

  disableEnableFormControlsAndRefreshState(form: UntypedFormGroup, disable: boolean) {

    if (!form)
      return;

    let defaultDisabledFieldsLabels: string[] = this.defaultDisabledFields().map(field => field.key as string)

    if ((this.allDisabled() == disable && defaultDisabledFieldsLabels?.length == 0) || (!form.controls || Object.keys(form.controls).length == 0))
      return;

    this.allDisabled.set(disable);

    Object.keys(form.controls).forEach((controlKey) => {
      const control = form.get(controlKey);

      if (!control) return;

      // Art des Geschäfts stays editable even after the line is saved (e.g. to correct a return 21 -> 22).
      // emitEvent false: a bare enable() emits valueChanges and would fire the auto-save with a synthetic change.
      if (ALWAYS_EDITABLE_LINE_ITEM_FIELDS.includes(controlKey)) {
        control.enable({ emitEvent: false });
        return;
      }

      if (defaultDisabledFieldsLabels.includes(controlKey)) {
        control.disable();
        return;
      }

      if (disable) {
        control.disable();
      } else {
        control.enable();
      }


    });

    this.refreshDependingFields();
  }

  setFieldLabelIfNeeded() {
    if (this.direction() && this.direction() == DocumentDirection.IN) {
      let field = findFieldByKey(this.fields(), "productText");
      if (field?.props?.label)
        field.props.label = (this.i18n.translate(_('document-line-item.productText-in')));
    }
  }

  /**
   * @description refreshes depending fields i.e. discountPercentages, storagePlace 
   * // IMPORTANT -> this function is called several times - always build in checks to prevent loops
   */
  refreshDependingFields() {
    if (this.ongoingRefreshDependingFields())
      return;

    this.ongoingRefreshDependingFields.set(true);

    if (this.dli()?.currentlyInEditMode()) {

      //refreshing specific fields, should not update any properties cuz of recursion
      let discountPercentageControl = this.form.get('discountPercentage');
      let discountPercentage2Control = this.form.get('discountPercentage2');

      let shouldDisableDiscounts = !!this.dli()?.nonDiscountable;

      if (shouldDisableDiscounts) {
        if (discountPercentageControl && discountPercentageControl.enabled) {
          discountPercentageControl.disable();
          if (discountPercentageControl.value != null)
            discountPercentageControl.setValue(null);
        }
        if (discountPercentage2Control && discountPercentage2Control.enabled) {
          discountPercentage2Control.disable();
          if (discountPercentage2Control.value != null)
            discountPercentage2Control.setValue(null);
        }
      }
      else {
        if (discountPercentageControl && !discountPercentageControl.enabled)
          discountPercentageControl.enable();
        if (discountPercentage2Control && !discountPercentage2Control.enabled)
          discountPercentage2Control.enable();
      }

      let storagePlaceControl = this.form.get('storagePlace');

      if (storagePlaceControl) {
        if ((this.dli()?.createdAt !== null && this.dli()?.createdAt !== undefined) && (this.stockAction() == StockAction.OUT || this.stockAction() == StockAction.IN)) {
          storagePlaceControl.disable();
        }
      }
    }
    this.ongoingRefreshDependingFields.set(false);
  }
}
